import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  SelectChangeEvent,
} from '@mui/material';
import { ArrowBack, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  defaultGuestAnalyticsRange,
  getGuestAnalyticsSummary,
  getGuestAnalyticsTimeseries,
  getGuestAnalyticsEntryMethods,
  getGuestAnalyticsGeography,
  getGuestAnalyticsFunnel,
  GuestAnalyticsSummaryDTO,
  GuestAnalyticsTimeseriesPointDTO,
  GuestAnalyticsEntryMethodDTO,
  GuestAnalyticsGeographyRowDTO,
  GuestAnalyticsFunnelStageDTO,
  GuestAnalyticsGranularity,
} from '../services/guestAnalyticsAdminService';

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Card elevation={2}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight="bold">
        {value.toLocaleString()}
      </Typography>
    </CardContent>
  </Card>
);

const PRESETS = [
  { label: 'Last 7 days', days: 6 },
  { label: 'Last 30 days', days: 29 },
  { label: 'Last 90 days', days: 89 },
];

const AdminGuestAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const defaults = defaultGuestAnalyticsRange();
  const [tab, setTab] = useState(0);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [granularity, setGranularity] = useState<GuestAnalyticsGranularity>('daily');
  const [geoLevel, setGeoLevel] = useState<'country' | 'council' | 'school'>('country');
  const [funnelEntryMethod, setFunnelEntryMethod] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<GuestAnalyticsSummaryDTO | null>(null);
  const [timeseries, setTimeseries] = useState<GuestAnalyticsTimeseriesPointDTO[]>([]);
  const [entryMethods, setEntryMethods] = useState<GuestAnalyticsEntryMethodDTO[]>([]);
  const [geography, setGeography] = useState<GuestAnalyticsGeographyRowDTO[]>([]);
  const [funnel, setFunnel] = useState<GuestAnalyticsFunnelStageDTO[]>([]);

  const dateParams = { from, to };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 0) {
        const [s, ts] = await Promise.all([
          getGuestAnalyticsSummary(dateParams),
          getGuestAnalyticsTimeseries({ ...dateParams, granularity }),
        ]);
        setSummary(s);
        setTimeseries(ts);
      } else if (tab === 1) {
        setEntryMethods(await getGuestAnalyticsEntryMethods(dateParams));
      } else if (tab === 2) {
        setGeography(await getGuestAnalyticsGeography({ ...dateParams, level: geoLevel }));
      } else {
        setFunnel(
          await getGuestAnalyticsFunnel({
            ...dateParams,
            entryMethod: funnelEntryMethod || undefined,
          })
        );
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [tab, from, to, granularity, geoLevel, funnelEntryMethod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyPreset = (days: number) => {
    const end = new Date();
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - days);
    setFrom(start.toISOString().slice(0, 10));
    setTo(end.toISOString().slice(0, 10));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Typography variant="h4" gutterBottom>
        Try as Guest Analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Default range: last 7 days including today (UTC). Catalog paths only — BYOK excluded.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            {PRESETS.map((p) => (
              <Button key={p.label} size="small" sx={{ mr: 1 }} onClick={() => applyPreset(p.days)}>
                {p.label}
              </Button>
            ))}
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
              Apply
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Entry methods" />
        <Tab label="Geography" />
        <Tab label="Funnel" />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && tab === 0 && summary && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2.4}>
              <StatCard label="Unique guests" value={summary.uniqueGuests} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard label="New sessions" value={summary.newSessions} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard label="Courses registered" value={summary.coursesRegistered} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard label="Topics accessed" value={summary.topicsAccessed} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <StatCard label="Signup CTAs" value={summary.signupCtasClicked} />
            </Grid>
          </Grid>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1">Guests over time</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={granularity}
              onChange={(_, v) => v && setGranularity(v)}
            >
              <ToggleButton value="daily">Daily</ToggleButton>
              <ToggleButton value="weekly">Weekly</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Paper sx={{ p: 2, height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeseries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="uniqueGuests" name="Unique guests" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Box>
      )}

      {!loading && tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Entry method</TableCell>
                <TableCell align="right">Guests</TableCell>
                <TableCell align="right">Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entryMethods.map((row) => (
                <TableRow key={row.entryMethod}>
                  <TableCell>{row.entryMethod}</TableCell>
                  <TableCell align="right">{row.guestCount}</TableCell>
                  <TableCell align="right">{row.sharePercent.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && tab === 2 && (
        <Box>
          <FormControl size="small" sx={{ mb: 2, minWidth: 160 }}>
            <InputLabel>Level</InputLabel>
            <Select
              label="Level"
              value={geoLevel}
              onChange={(e: SelectChangeEvent) =>
                setGeoLevel(e.target.value as 'country' | 'council' | 'school')
              }
            >
              <MenuItem value="country">By country</MenuItem>
              <MenuItem value="council">By council</MenuItem>
              <MenuItem value="school">By school</MenuItem>
            </Select>
          </FormControl>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Guests</TableCell>
                  <TableCell align="right">% registered</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {geography.map((row) => (
                  <TableRow key={`${row.locationName}-${row.locationId ?? 'unknown'}`}>
                    <TableCell>{row.locationName}</TableCell>
                    <TableCell align="right">{row.guestCount}</TableCell>
                    <TableCell align="right">{row.registrationPercent.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {!loading && tab === 3 && (
        <Box>
          <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
            <InputLabel>Entry method filter</InputLabel>
            <Select
              label="Entry method filter"
              value={funnelEntryMethod}
              onChange={(e: SelectChangeEvent) => setFunnelEntryMethod(e.target.value)}
            >
              <MenuItem value="">All catalog</MenuItem>
              <MenuItem value="SEARCH">Search</MenuItem>
              <MenuItem value="BY_LOCATION">By school</MenuItem>
              <MenuItem value="QUICK_COURSE">Quick course</MenuItem>
            </Select>
          </FormControl>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Stage</TableCell>
                  <TableCell align="right">Guests</TableCell>
                  <TableCell align="right">Conversion to next</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {funnel.map((row) => (
                  <TableRow key={row.stageId}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell align="right">{row.guestCount}</TableCell>
                    <TableCell align="right">
                      {row.conversionToNextPercent != null
                        ? `${row.conversionToNextPercent.toFixed(1)}%`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Container>
  );
};

export default AdminGuestAnalyticsPage;
